import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { CmmdProjectHome } from "../components/projects/CmmdProjectHome";
import { useBlockedBoardExit } from "../hooks/useProjectRouteTarget";
import { isProjectTab, type ProjectTab } from "../components/projects/CmmdProjectTabs";
import { parseTaskSearchParam } from "../components/projects/taskSearchParam";
import type { ForgeThreadTarget } from "../components/projects/forgeExecutionTarget";

/**
 * A CMMD board, viewed while working in a checkout of `projectKey`.
 *
 * Two ids side by side rather than nested ownership: the board is not owned by
 * the Forge project, they are peers joined by a repository. `cmmdProjectId` is
 * also already the scope key for saved views (`savedViewScope`), so this
 * segment states a scope that exists in the data rather than inventing one.
 */
export const Route = createFileRoute("/_chat/project/$projectKey/tasks/$cmmdProjectId")({
  validateSearch: (search: Record<string, unknown>): { task?: string; tab?: ProjectTab } => {
    const task = parseTaskSearchParam(search["task"]);
    const tab = search["tab"];
    return {
      ...(task !== undefined ? { task } : {}),
      ...(isProjectTab(tab) ? { tab } : {}),
    };
  },
  component: ProjectBoardRouteView,
});

/** Wires the route's `tab`/`task` search params into `CmmdProjectHome`. */
function ProjectBoardRouteView() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { projectKey, cmmdProjectId } = Route.useParams();
  const exit = useBlockedBoardExit(projectKey);
  const { tab, task } = Route.useSearch();

  return (
    <CmmdProjectHome
      cmmdProjectId={cmmdProjectId}
      requestedTab={tab ?? null}
      requestedTaskId={task ?? null}
      onClearRequestedTaskParam={() => {
        // Through the router's own search state, not just `window.history`:
        // a later navigation that carries the current search forward (e.g.
        // `search: (prev) => prev`) would otherwise resurrect `?task=` and
        // replay the launch. `tab` is dropped in the same step: a handoff
        // link always opens on Tasks (see `CmmdProjectHome`'s `activeTab`),
        // so once the launch is consumed, a stale `?tab=` would disagree
        // with what is on screen and send a refresh to the wrong tab.
        void navigate({
          search: (previous) => {
            const { task: _task, tab: _tab, ...rest } = previous;
            return rest;
          },
          replace: true,
        });
      }}
      onOpenForgeThread={(target: ForgeThreadTarget) => {
        void navigate({
          to: "/$environmentId/$threadId",
          params: { environmentId: target.environmentId, threadId: target.threadId },
        });
      }}
      onLeaveProject={() => {
        // Back to the group when a connected checkout renders it. When only
        // this board vouches for the group, the landing would redirect straight
        // back here, so the exit is home instead (forge#3491).
        if (exit.kind === "group") {
          void navigate({
            to: "/project/$projectKey",
            params: { projectKey: exit.groupKey },
            replace: true,
          });
          return;
        }
        void navigate({ to: "/", replace: true });
      }}
    />
  );
}
